/**
 * 魔珐星云具身驱动 Lite SDK 动态加载
 * @see https://xingyun3d.com/developers/52-183
 */

const SDK_GLOBAL = 'XmovAvatar'

/** 是否微信内置浏览器 */
export function isWeChatInApp() {
  if (typeof navigator === 'undefined') return false
  return /MicroMessenger/i.test(navigator.userAgent || '')
}

function isMobileUa() {
  if (typeof navigator === 'undefined') return false
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')
}

/** 微信 Windows/Mac 客户端内置浏览器（非手机微信） */
export function isWeChatDesktopBrowser() {
  if (!isWeChatInApp() || isMobileUa()) return false
  const ua = navigator.userAgent || ''
  return /Windows|Win64|WOW64|Macintosh|Mac OS X|Linux|X11/i.test(ua)
}

/**
 * 桌面渲染策略：Chrome 桌面 + 微信电脑版
 * （不在 init 抢切 idle、用 onStateRenderChange 续待机循环）
 */
export function shouldUsePcAvatarRenderStrategy() {
  if (typeof window === 'undefined') return false
  if (isWeChatDesktopBrowser()) return true
  if (isMobileUa()) return false
  if (isWeChatInApp()) return false
  return true
}

/** 手机微信 / 手机浏览器：init 重算布局、Worker 补丁等 */
export function shouldUseMobileAvatarRenderStrategy() {
  return !shouldUsePcAvatarRenderStrategy()
}

/** @deprecated 请用 shouldUseMobileAvatarRenderStrategy */
export function needsWebCodecsCompatPatch() {
  return shouldUseMobileAvatarRenderStrategy()
}

/** 统一不传，由 SDK/浏览器自选 */
export function getSdkHardwareAcceleration() {
  return undefined
}

/** 微信内（含电脑版）不认 hardwareAcceleration 枚举，需主线程 strip */
function needsVideoDecoderStrip() {
  if (isWeChatInApp()) return true
  if (isMobileUa()) return true
  if (typeof window !== 'undefined' && window.innerWidth <= 768) return true
  return false
}

/** 仅手机微信/手机浏览器注入 Worker Blob；微信电脑版/桌面 Chrome 注入会几帧后停画 */
function shouldPatchWorkerBlob() {
  if (typeof window === 'undefined') return false
  if (isWeChatDesktopBrowser()) return false
  if (isWeChatInApp()) return true
  if (isMobileUa()) return true
  return window.innerWidth <= 768
}

const WEBCODECS_WORKER_PREAMBLE = `
;(function(global){
  var g = global || (typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : {}));
  if (!g || g.__xmovCompatPatched) return;
  g.__xmovCompatPatched = true;

  if (typeof VideoDecoder !== 'undefined') {
    var strip = function(c){
      if (!c || typeof c !== 'object') return c;
      var n = Object.assign({}, c);
      delete n.hardwareAcceleration;
      return n;
    };
    if (VideoDecoder.isConfigSupported) {
      var origSupported = VideoDecoder.isConfigSupported.bind(VideoDecoder);
      VideoDecoder.isConfigSupported = function(c){ return origSupported(strip(c)); };
    }
    if (VideoDecoder.prototype && VideoDecoder.prototype.configure) {
      var origCfg = VideoDecoder.prototype.configure;
      VideoDecoder.prototype.configure = function(c){ return origCfg.call(this, strip(c)); };
    }
  }

  var origPostMessage = g.postMessage;
  if (typeof origPostMessage === 'function') {
    function filterTransferList(transfer) {
      if (transfer == null) return undefined;
      var list = Array.isArray(transfer) ? transfer : [transfer];
      if (!list.length) return undefined;
      var valid = [];
      for (var i = 0; i < list.length; i++) {
        var t = list[i];
        try {
          if (t instanceof ArrayBuffer) valid.push(t);
          else if (typeof ImageBitmap !== 'undefined' && t instanceof ImageBitmap) valid.push(t);
          else if (typeof MessagePort !== 'undefined' && t instanceof MessagePort) valid.push(t);
          else if (typeof OffscreenCanvas !== 'undefined' && t instanceof OffscreenCanvas) valid.push(t);
        } catch (e) { /* ignore */ }
      }
      if (valid.length === list.length) return valid;
      return undefined;
    }
    g.postMessage = function(message, transfer) {
      var filtered = filterTransferList(transfer);
      if (filtered && filtered.length) {
        return origPostMessage.call(g, message, filtered);
      }
      return origPostMessage.call(g, message);
    };
  }
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this));
`

function shouldInjectWorkerPreamble(parts, opts) {
  if (!Array.isArray(parts) || !parts.length || typeof parts[0] !== 'string') return false
  const head = parts[0]
  if (head.indexOf('__xmovCompatPatched') !== -1) return false
  // 勿对 AudioWorklet / PCM 播放器脚本注入补丁，否则真机无声音
  if (
    head.includes('pcm-audio') ||
    head.includes('AudioWorklet') ||
    head.includes('registerProcessor') ||
    head.includes('AudioWorkletProcessor')
  ) {
    return false
  }
  if (head.includes('VideoDecoder') || head.includes('mp4box') || head.includes('isConfigSupported')) {
    return true
  }
  return false
}

/**
 * 微信/XWeb：主线程 strip hardwareAcceleration；
 * Worker Blob 补丁仅手机端，微信电脑版不注入（否则有音无画/播几帧就停）
 */
export function patchWebCodecsHardwarePreference() {
  if (typeof window === 'undefined') return

  const stripVd = needsVideoDecoderStrip()
  const patchBlob = shouldPatchWorkerBlob()
  if (!stripVd && !patchBlob) return

  const strip = (config) => {
    if (!config || typeof config !== 'object') return config
    const next = { ...config }
    delete next.hardwareAcceleration
    return next
  }

  if (stripVd && !window.__xmovVdStripped) {
    window.__xmovVdStripped = true
    const VD = window.VideoDecoder
    if (VD) {
      if (typeof VD.isConfigSupported === 'function') {
        const orig = VD.isConfigSupported.bind(VD)
        VD.isConfigSupported = (config) => orig(strip(config))
      }
      if (VD.prototype && typeof VD.prototype.configure === 'function') {
        const origConfigure = VD.prototype.configure
        VD.prototype.configure = function patchedConfigure(config) {
          return origConfigure.call(this, strip(config))
        }
      }
    }
  }

  if (patchBlob) {
    const NativeBlob = window.Blob
    if (NativeBlob && !NativeBlob.__xmovPatched) {
      const WrappedBlob = function (parts, options) {
        const opts = options || {}
        if (shouldInjectWorkerPreamble(parts, opts)) {
          parts = [WEBCODECS_WORKER_PREAMBLE + parts[0], ...parts.slice(1)]
        }
        return new NativeBlob(parts, opts)
      }
      WrappedBlob.prototype = NativeBlob.prototype
      WrappedBlob.__xmovPatched = true
      window.Blob = WrappedBlob
    }
  }
}

/** 进入待机可互动状态（口型/待机动画） */
export function enterInteractiveIdle(instance) {
  if (!instance) return
  try {
    if (typeof instance.interactiveidle === 'function') {
      instance.interactiveidle()
    } else if (typeof instance.interactive_idle === 'function') {
      instance.interactive_idle()
    } else if (typeof instance.idle === 'function') {
      instance.idle()
    }
  } catch (_) { /* ignore */ }
}

let pcAvatarSpeaking = false
let pcIdleLoopTimer = null

export function setPcAvatarSpeaking(speaking) {
  pcAvatarSpeaking = !!speaking
}

export function clearPcIdleLoopTimer() {
  if (pcIdleLoopTimer) {
    clearTimeout(pcIdleLoopTimer)
    pcIdleLoopTimer = null
  }
}

/** PC：待机动画片段播完后再次 interactiveidle，防止循环几帧后卡住 */
function schedulePcIdleLoopRefresh(instance, durationMs) {
  if (!instance || !shouldUsePcAvatarRenderStrategy() || pcAvatarSpeaking) return
  clearPcIdleLoopTimer()
  const wait = Math.max(200, Number(durationMs) + 60)
  pcIdleLoopTimer = setTimeout(() => {
    pcIdleLoopTimer = null
    if (!pcAvatarSpeaking) {
      enterInteractiveIdle(instance)
    }
  }, wait)
}

function handlePcStateRenderChange(instance, state, duration) {
  if (!instance || !shouldUsePcAvatarRenderStrategy() || pcAvatarSpeaking) return
  const s = String(state || '').toLowerCase()
  if (!s.includes('idle') && !s.includes('interactive')) return
  const ms = Number(duration)
  if (Number.isFinite(ms) && ms > 0) {
    schedulePcIdleLoopRefresh(instance, ms)
  }
}

/** 微信电脑版部分版本 onStateRenderChange 无 duration，用 state 变化续待机 */
function handlePcStateChange(instance, state) {
  if (!instance || !shouldUsePcAvatarRenderStrategy() || pcAvatarSpeaking) return
  const s = String(state || '').toLowerCase()
  if (
    s.includes('interactive') ||
    s.includes('idle') ||
    s === 'end' ||
    s.includes('voice_end')
  ) {
    schedulePcIdleLoopRefresh(instance, 1200)
  }
}

/** PC 播报结束后恢复待机动画 */
export function resumePcAvatarAfterSpeak(instance) {
  if (!instance || !shouldUsePcAvatarRenderStrategy()) return
  setPcAvatarSpeaking(false)
  clearPcIdleLoopTimer()
  setTimeout(() => enterInteractiveIdle(instance), 120)
}

let sharedUnlockCtx = null
let weixinAudioUnlockBound = false

/** 微信 JSBridge 就绪后再解锁一次音频（解决手机微信有画面无声音） */
export function bindWeixinAudioUnlock() {
  if (typeof window === 'undefined' || weixinAudioUnlockBound) return
  weixinAudioUnlockBound = true
  const run = () => {
    unlockPageAudio()
    try {
      window.WeixinJSBridge?.invoke('getNetworkType', {}, () => {
        unlockPageAudio()
      })
    } catch (_) { /* ignore */ }
  }
  if (window.WeixinJSBridge) {
    run()
  } else {
    document.addEventListener('WeixinJSBridgeReady', run, { once: true })
  }
}

/** 在用户点击后解锁 WebAudio（微信内需手势触发，否则 PCM 播放器无法 init） */
export function unlockPageAudio() {
  if (typeof window === 'undefined') return Promise.resolve()
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return Promise.resolve()
    if (!sharedUnlockCtx || sharedUnlockCtx.state === 'closed') {
      sharedUnlockCtx = new Ctx()
    }
    const ctx = sharedUnlockCtx
    const go = () => {
      try {
        const buf = ctx.createBuffer(1, 1, 22050)
        const src = ctx.createBufferSource()
        src.buffer = buf
        src.connect(ctx.destination)
        src.start(0)
      } catch (_) { /* ignore */ }
    }
    if (ctx.state === 'suspended') {
      return ctx.resume().then(go).catch(() => {})
    }
    go()
    return Promise.resolve()
  } catch (_) {
    return Promise.resolve()
  }
}

export function loadXmovAvatarScript(sdkUrl) {
  patchWebCodecsHardwarePreference()
  const url = sdkUrl || 'https://media.xingyun3d.com/xingyun3d/general/litesdk/xmovAvatar@latest.js'
  if (typeof window !== 'undefined' && window[SDK_GLOBAL]) {
    return Promise.resolve(window[SDK_GLOBAL])
  }
  const existing = document.querySelector(`script[data-xmov-sdk="1"]`)
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(window[SDK_GLOBAL]))
      existing.addEventListener('error', reject)
      if (window[SDK_GLOBAL]) resolve(window[SDK_GLOBAL])
    })
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    script.dataset.xmovSdk = '1'
    script.onload = () => {
      if (window[SDK_GLOBAL]) {
        resolve(window[SDK_GLOBAL])
      } else {
        reject(new Error('XmovAvatar 未挂载到 window'))
      }
    }
    script.onerror = () => reject(new Error('数字人 SDK 脚本加载失败'))
    document.head.appendChild(script)
  })
}

/**
 * 创建并初始化 SDK 实例
 */
export async function createXmovAvatarInstance(options) {
  patchWebCodecsHardwarePreference()
  await unlockPageAudio()
  const XmovAvatar = await loadXmovAvatarScript(options.config?.sdkUrl)
  const { appId, appSecret, gatewayServer } = options.config || {}
  if (!appId || !appSecret) {
    throw new Error('数字人 appId / appSecret 未配置')
  }

  const instance = new XmovAvatar({
    containerId: options.containerId,
    appId,
    appSecret,
    gatewayServer: gatewayServer || 'https://nebula-agent.xingyun3d.com/user/v1/ttsa/session',
    enableLogger: false,
    hardwareAcceleration: getSdkHardwareAcceleration(),
    onMessage(message) {
      options.onMessage?.(message)
    },
    onStateChange(state) {
      options.onStateChange?.(state)
      handlePcStateChange(instance, state)
    },
    onStatusChange(status) {
      options.onStatusChange?.(status)
    },
    onStateRenderChange(state, duration) {
      options.onStateRenderChange?.(state, duration)
      handlePcStateRenderChange(instance, state, duration)
    },
    onVoiceStateChange(status) {
      options.onVoiceStateChange?.(status)
    },
    onWidgetEvent(data) {
      options.onWidgetEvent?.(data)
    },
    proxyWidget: {
      subtitle_on: () => {},
      subtitle_off: () => {},
      widget_pic: (data) => {
        options.onBackgroundImage?.(data)
      }
    }
  })

  await new Promise((resolve, reject) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      // 留时间给 SDK 按容器 100%×100% 计算 canvas 偏移与 scale（文档 2.4）
      setTimeout(() => resolve(), 400)
    }
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true
        resolve()
      }
    }, 60000)

    try {
      const ret = instance.init({
        onDownloadProgress(progress) {
          options.onDownloadProgress?.(progress)
          if (Number(progress) >= 100) {
            finish()
          }
        },
        initModel: 'normal'
      })
      if (ret && typeof ret.then === 'function') {
        ret.then(finish).catch(reject)
      }
    } catch (e) {
      clearTimeout(timer)
      reject(e)
    }
  })

  await unlockPageAudio()
  try {
    if (typeof instance.setVolume === 'function') {
      instance.setVolume(1)
    }
  } catch (_) { /* ignore */ }

  if (shouldUseMobileAvatarRenderStrategy()) {
    enterInteractiveIdle(instance)
  } else {
    // 微信电脑版：开场播完后补一次待机（不连调，避免卡帧）
    setTimeout(() => {
      if (!pcAvatarSpeaking) {
        enterInteractiveIdle(instance)
      }
    }, 2800)
  }

  return instance
}

/** 打断当前播报并回到可交互待机（发新消息前调用） */
export function cancelActiveSpeak(instance) {
  if (!instance) return
  try {
    if (typeof instance.interactiveidle === 'function') {
      instance.interactiveidle()
    } else if (typeof instance.interactive_idle === 'function') {
      instance.interactive_idle()
    } else if (typeof instance.idle === 'function') {
      instance.idle()
    }
  } catch (_) { /* ignore */ }
}

/** 非流式一句话（须在用户手势链路上 await，微信内才能出声） */
export async function speakOnce(instance, text) {
  if (!instance || !text?.trim()) return
  await unlockPageAudio()
  setPcAvatarSpeaking(true)
  const t = text.trim()
  instance.speak(t, true, true)
}

export function splitSpeakChunks(text, maxLen = 120) {
  const s = (text || '').trim()
  if (!s) return []
  const parts = s.split(/(?<=[。！？；\n])/g).filter((p) => p && p.trim())
  const chunks = []
  let buf = ''
  for (const p of parts) {
    const piece = p.trim()
    if (!piece) continue
    if ((buf + piece).length > maxLen && buf) {
      chunks.push(buf)
      buf = piece
    } else {
      buf += piece
    }
  }
  if (buf) chunks.push(buf)
  return chunks.length ? chunks : [s]
}

function isVoiceEndStatus(status) {
  const s = String(status || '').toLowerCase()
  return s === 'end' || s === 'voice_end' || s === 'voiceend'
}

/**
 * 整段回复播报：按句切分，等 voice_end 再说下一句（微信 PC/手机音画更稳）
 * @returns {{ start: Function, handleVoiceState: Function, reset: Function }}
 */
export function createQueuedSpeaker(instance) {
  let chunks = []
  let index = 0
  let started = false
  let waitingVoiceEnd = false

  const speakCurrent = () => {
    if (!instance || index >= chunks.length) return
    const text = chunks[index]
    const isStart = index === 0
    const isEnd = index === chunks.length - 1
    instance.speak(text, isStart, isEnd)
    if (!isEnd) {
      waitingVoiceEnd = true
    } else {
      waitingVoiceEnd = false
      index = chunks.length
    }
  }

  const tryIdle = () => {
    try {
      if (typeof instance?.interactiveidle === 'function') {
        instance.interactiveidle()
      } else if (typeof instance?.interactive_idle === 'function') {
        instance.interactive_idle()
      }
    } catch (_) { /* ignore */ }
  }

  return {
    reset() {
      chunks = []
      index = 0
      started = false
      waitingVoiceEnd = false
    },
    async start(fullText) {
      const t = (fullText || '').trim()
      if (!t || !instance) return
      await unlockPageAudio()
      chunks = splitSpeakChunks(t)
      if (!chunks.length) return
      index = 0
      started = true
      waitingVoiceEnd = false
      setPcAvatarSpeaking(true)
      if (shouldUseMobileAvatarRenderStrategy()) {
        try {
          if (typeof instance.think === 'function') {
            instance.think()
          }
        } catch (_) { /* ignore */ }
      }
      speakCurrent()
    },
    handleVoiceState(status) {
      if (!started || !waitingVoiceEnd || !isVoiceEndStatus(status)) return
      waitingVoiceEnd = false
      index += 1
      if (index < chunks.length) {
        if (shouldUseMobileAvatarRenderStrategy()) {
          tryIdle()
        }
        setTimeout(speakCurrent, isWeChatInApp() ? 120 : 60)
      } else if (shouldUsePcAvatarRenderStrategy()) {
        resumePcAvatarAfterSpeak(instance)
      } else {
        setTimeout(() => enterInteractiveIdle(instance), isWeChatInApp() ? 120 : 60)
      }
    },
    isSpeaking() {
      return started && (waitingVoiceEnd || index < chunks.length)
    }
  }
}

/** @deprecated 连续 speak 易触发 40007，请用 createQueuedSpeaker */
export async function speakReply(instance, fullText) {
  if (!instance || !fullText?.trim()) return
  const speaker = createQueuedSpeaker(instance)
  speaker.start(fullText)
}

export function destroyXmovInstance(instance) {
  clearPcIdleLoopTimer()
  setPcAvatarSpeaking(false)
  if (!instance) return
  try {
    if (typeof instance.offlineMode === 'function') {
      instance.offlineMode()
    }
  } catch (_) { /* ignore */ }
  try {
    instance.destroy?.()
  } catch (_) {
    /* ignore */
  }
}

if (typeof window !== 'undefined') {
  patchWebCodecsHardwarePreference()
}
